import React from 'react';
import { Brain, Clock, Shield, BookMarked } from 'lucide-react';

const MEMORY_CARDS = [
  {
    id: 'patterns',
    icon: Brain,
    iconColor: '#C9A84C',
    bgColor: 'rgba(201,168,76,0.15)',
    title: 'Your Patterns',
    description: 'Sage notices recurring triggers and themes across sessions — and gently surfaces them when the time is right.',
  },
  {
    id: 'journey',
    icon: Clock,
    iconColor: '#e8b4b8',
    bgColor: 'rgba(232,180,184,0.15)',
    title: 'Your Journey',
    description: 'Every milestone, every breakthrough, every hard day — tracked and honored as part of your healing story.',
  },
  {
    id: 'strengths',
    icon: Shield,
    iconColor: '#7d9d8c',
    bgColor: 'rgba(125,157,140,0.15)',
    title: 'Your Strengths',
    description: 'Sage recognizes your resilience even when you cannot see it — and reflects it back when you need it most.',
  },
  {
    id: 'story',
    icon: BookMarked,
    iconColor: '#C9A84C',
    bgColor: 'rgba(201,168,76,0.15)',
    title: 'Your Story',
    description: 'You never have to re-explain yourself. Sage carries your full context into every single conversation.',
  },
];

const MemoryCard = ({ card, delay }) => {
  const Icon = card.icon;
  
  return (
    <div 
      className="memory-card opacity-0 translate-y-8 transition-all duration-700 ease-out p-6 md:p-8 rounded-2xl"
      style={{ 
        background: '#F9F4F0',
        border: '1px solid #F3EEE8',
        transitionDelay: `${delay}ms`
      }}
      data-testid={`memory-card-${card.id}`}
    >
      <div 
        className="w-12 h-12 rounded-full flex items-center justify-center mb-5"
        style={{ background: card.bgColor }}
      >
        <Icon className="w-6 h-6" style={{ color: card.iconColor }} strokeWidth={1.5} />
      </div>
      <h3 
        className="text-xl font-medium mb-3"
        style={{ fontFamily: 'Playfair Display, serif', color: '#1A1A2E' }}
      >
        {card.title}
      </h3>
      <p 
        className="text-sm leading-relaxed"
        style={{ color: '#4A4A5A' }}
      >
        {card.description}
      </p>
    </div>
  );
};

export const MemorySection = () => {
  return (
    <section className="py-16 md:py-24 bg-[#FBF9F6] overflow-hidden">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12 md:mb-16">
          <p 
            className="text-xs tracking-[0.2em] uppercase mb-4"
            style={{ color: '#C9A84C' }}
          >
            Continuous Memory
          </p>
          <h2
            className="text-2xl md:text-3xl lg:text-4xl font-normal tracking-tight leading-relaxed"
            style={{ fontFamily: 'Playfair Display, serif', color: '#1A1A2E' }}
          >
            She remembers what most people<br className="hidden md:block" /> forget to notice
          </h2>
        </div>

        {/* 2x2 Memory Cards Grid */}
        <div className="grid md:grid-cols-2 gap-5 md:gap-6 mb-12">
          {MEMORY_CARDS.map((card, index) => (
            <MemoryCard key={card.id} card={card} delay={index * 150} />
          ))}
        </div>

        {/* Quote */}
        <div className="text-center">
          <p 
            className="text-lg md:text-xl italic leading-relaxed"
            style={{ fontFamily: 'Playfair Display, serif', color: '#4A4A5A' }}
          >
            "Most apps forget you the moment you close them.<br />
            <span style={{ color: '#C9A84C' }}>Sage carries your story forward.</span>"
          </p>
        </div>
      </div>

      {/* Animation Styles */}
      <style>{`
        .memory-card.animate-in {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>
    </section>
  );
};

export default MemorySection;
